import PropTypes from 'prop-types';
import { CartFooter, CartItem, DangerousHtmlContent } from '@eti/components';
import { useSiteContext, useTranslation } from '@eti/providers';
import { css, etiColors } from '@eti/styles';
import { constants, price as priceUtil } from '@eti/utils';
import { AddOnOrder, PriceFormat } from '@eti/types';
import { Stack } from '@etg/wings';
import { ALL, NONE, SOME } from '../../../constants/transportationMeansConstants';
import { getTravelerPricesTotal } from '../../../utils/travelers';
import { getSelectedPaymentMethodLabel } from '../../../utils/paymentMethods';
import { tripIncludesTrainSegments } from '../../../widgets/trip-details/utils/format';
import { bounds as boundsPropTypes } from '../../../widgets/trip-details/propTypes';
import { paymentMethodTypes } from '../../payment/constants/paymentMethods';
import { transformCartProducts } from '../utils/receiptCartProductsUtil';
import { PaymentTypes } from '../utils/constants';
import ReceiptFlightInformation from './price-specification/ReceiptFlightInformation';
import ReceiptTravelers from './price-specification/ReceiptTravelersTextResolver';
import ReceiptProducts from './price-specification/ReceiptProducts';
import ReceiptProductBundle from './price-specification/ReceiptProductBundle';
import PostBookingOrder from './PostBookingOrder';

const { BANK, CARD } = paymentMethodTypes;

const { unicodeControlCharacters, MOUSE_FLOW_CLASS_NAMES } = constants;
const { LRE, PDF } = unicodeControlCharacters;

const itemStyles = css`
  border-color: ${etiColors.grayScale[8]};
  margin-left: 0;
  margin-right: 0;
`;

const Item = (props) => <CartItem className={itemStyles} {...props} />;

const flightsContentStyles = css`
  padding-left: 1.5rem;

  [dir='rtl'] & {
    padding-left: 0;
    padding-right: 1.5rem;
  }
`;

const productItemStyles = css`
  border-color: ${etiColors.grayScale[8]};
  margin: 0;

  &:last-child {
    border-bottom: 1px dotted ${etiColors.grayScale[8]};
  }
`;

const ProductItem = (props) => <CartItem className={productItemStyles} {...props} />;

const productIconStyles = css`
  height: 1rem;
  width: 1rem;
`;

const paymentContentStyles = css`
  padding-top: 0.25rem;
`;

const footerStyles = css`
  background-color: ${etiColors.grayScale[2]};
`;

const footerContentStyles = css`
  font-size: 0.75rem;
  padding-top: 0.25rem;
`;

const totalInChargeCurrencyStyles = css`
  display: flex;
  margin-bottom: 10px;
`;

const totalInChargeCurrencyLabelStyles = css`
  display: flex;
  flex: 1;
  font-size: 0.875rem;
`;

const totalInChargeCurrencyPriceStyles = css`
  display: flex;
  font-size: 1rem;
  font-weight: 700;
`;

export const transformPaymentTypes = (paymentTypes = [], labels) =>
  paymentTypes.map(
    ({
      name: paymentName,
      type: paymentType,
      cardSubType,
      maskedCardNumber,
      price,
      productSummaryAlternativeName,
    }) => {
      let name;
      let type;
      let testIdName;

      const { paymentMethodCreditCardText, paymentMethodBankText } = labels;

      if (paymentType === PaymentTypes.CreditCard) {
        type = CARD;
        const subType = cardSubType || '';
        name = subType || paymentName;
        testIdName = `${paymentMethodCreditCardText}${subType}`;
      } else {
        name = paymentName;
        type = paymentType;

        if (paymentType === BANK) {
          testIdName = paymentMethodBankText;
        } else if (paymentName === PaymentTypes.Wallet) {
          // Type and Name come vice-versa for Wallet payment methods!
          testIdName = 'wallet';
        } else {
          testIdName = 'other';
        }
      }

      const paymentMethodInformation = [
        { type, name },
        { ...labels, productSummaryAlternativeName },
      ];

      return {
        title: getSelectedPaymentMethodLabel(...paymentMethodInformation),
        type,
        priceDataTestId: `orderPage-paymentMethod-${testIdName}-price`,
        maskedCardNumber,
        price: priceUtil.formatPrice(price?.price?.value),
      };
    },
  );

const formatTransactionAmount = (transactionAmount, siteCurrency, locale) => {
  const { price } = transactionAmount;
  if (siteCurrency.code === price.currency.code) {
    return priceUtil.formatPrice(price.value);
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: price.currency.code,
  }).format(price.value / 10 ** price.currency.exponent);
};

const ReceiptPriceSpecification = ({
  addOnOrders,
  bounds,
  bundles,
  cc2carrierBreakdown,
  chargeCurrencyPriceFormat,
  discountPrice,
  instalmentsTotalFee,
  isOtherChargeCurrency,
  marketingCarrierNames,
  orderExtraProducts,
  paymentTypes,
  priceInChargeCurrency,
  totalPrice,
  totalPriceIncludingAddOnOrders,
  travelerPrices,
}) => {
  const { t } = useTranslation();
  const { language, siteCurrency } = useSiteContext();
  const travelerPricesTotal = getTravelerPricesTotal(travelerPrices);
  const paymentLabels = {
    paymentMethodBankText: t('Payment.Method.Bank.Text'),
    paymentMethodCreditCardText: t('Receipt.ReceiptDetails.Label.PaymentType.Card'),
    paymentMethodKlarnaPayLaterText: t('Payment.Method.Klarna.PayLater.Text'),
    paymentMethodKlarnaPayNowText: t('Payment.Method.Klarna.PayNow.Text'),
    paymentMethodKlarnaSliceItText: t('Payment.Method.Klarna.SliceIt.Text'),
    paymentMethodKlarnaSofortText: t('Payment.Method.Klarna.Sofortuberweisung.Text'),
    paymentMethodPayInternalText: t('Payment.Method.PayInternal.Text'),
  };

  const hasAddOnOrders = addOnOrders?.length;

  const sumLabel = hasAddOnOrders
    ? t('Order.Receipt.SubTotal.Header')
    : t('Receipt.ReceiptDetails.Label.Sum');

  const isCC2COrder = Boolean(cc2carrierBreakdown && cc2carrierBreakdown.length > 0);

  const icon = {
    [NONE]: 'plane-rotated',
    [SOME]: 'tag',
    [ALL]: 'train',
  };

  const uniqueCarrierNames =
    isCC2COrder &&
    Array.from(new Set(cc2carrierBreakdown.map(({ carrierName }) => carrierName))).join(', ');

  const chargeCurrencyPriceFormatter =
    isOtherChargeCurrency && priceUtil.createPriceFormatter(chargeCurrencyPriceFormat);

  return (
    <Stack className="etiCart">
      <div className={MOUSE_FLOW_CLASS_NAMES}>
        <Item
          icon={icon[tripIncludesTrainSegments(bounds)]}
          price={priceUtil.formatPrice(travelerPricesTotal)}
          title={t('IbeClient.Cart.TripPrice.Header')}
        >
          <div className={flightsContentStyles}>
            <ReceiptFlightInformation bounds={bounds} />
            <ReceiptTravelers
              marketingCarrierNames={marketingCarrierNames}
              travelerPrices={travelerPrices}
            />
          </div>
        </Item>
        {bundles.length > 0 &&
          bundles.map((bundle) => (
            <ReceiptProductBundle
              component={Item}
              id={bundle.id}
              key={bundle.id}
              price={bundle.price}
              products={bundle.products}
              title={bundle.title}
            />
          ))}
        {orderExtraProducts && (
          <ReceiptProducts
            component={ProductItem}
            iconStyles={productIconStyles}
            products={transformCartProducts(
              t,
              orderExtraProducts,
              bounds,
              travelerPrices?.reduce((prev, acc) => acc.quantity + prev, 0) ?? 0,
            )}
          />
        )}
        {transformPaymentTypes(paymentTypes, paymentLabels).map(
          ({ maskedCardNumber, title, price, type, priceDataTestId }) => (
            <Item key={type} price={price} priceDataTestId={priceDataTestId} title={title}>
              {maskedCardNumber && (
                <div className={paymentContentStyles}>{`${LRE}${maskedCardNumber}${PDF}`}</div>
              )}
            </Item>
          ),
        )}
        {isCC2COrder &&
          cc2carrierBreakdown.map(({ carrierName, transactionAmount }) => (
            <Item
              key={`${carrierName}-${transactionAmount.price.value}`}
              price={formatTransactionAmount(transactionAmount, siteCurrency, language.code)}
              priceDataTestId="cc2carrier-transaction-entry"
              title={carrierName}
            />
          ))}
        {discountPrice && (
          <Item
            price={priceUtil.formatPrice(discountPrice)}
            priceDataTestId="orderPage-discountPrice"
            title={t('Receipt.Discount.Label')}
          />
        )}
        {instalmentsTotalFee > 0 && (
          <Item
            price={priceUtil.formatPrice(instalmentsTotalFee)}
            priceDataTestId="orderPage-instalmentsTotalFee"
            title={t('Payment.CardInstalments.PaymentPlan.InstalmentFee.Label')}
          />
        )}
        <Item
          price={<strong>{priceUtil.formatPrice(totalPrice)}</strong>}
          priceDataTestId="orderPage-totalPrice"
          title={sumLabel}
        />
        {hasAddOnOrders ? (
          <div data-testid="orderPage-postBooking-orders">
            {addOnOrders.map((addOnOrder) => {
              const transformedExtraProducts = transformCartProducts(
                t,
                addOnOrder.extraProducts,
                bounds,
                travelerPrices?.reduce((prev, acc) => acc.quantity + prev, 0) ?? 0,
              );

              return (
                <PostBookingOrder
                  addOnOrder={{
                    ...addOnOrder,
                    extraProducts: transformedExtraProducts,
                  }}
                  key={`${addOnOrder.orderNumber}`}
                />
              );
            })}
          </div>
        ) : null}
      </div>
      <CartFooter
        className={footerStyles}
        price={priceUtil.formatPrice(totalPriceIncludingAddOnOrders)}
        title={`${t('Receipt.ReceiptDetails.Label.Paid')}:`}
      >
        <div className={footerContentStyles}>
          {isOtherChargeCurrency && (
            <div
              className={totalInChargeCurrencyStyles}
              data-testid="orderPage-totalInChargeCurrency"
            >
              <span className={totalInChargeCurrencyLabelStyles}>
                {`${t('Receipt.ReceiptDetails.Label.TotalInChargeCurrency')}:`}
              </span>
              <span className={totalInChargeCurrencyPriceStyles}>
                {chargeCurrencyPriceFormatter.formatPrice(priceInChargeCurrency)}
              </span>
            </div>
          )}

          <DangerousHtmlContent
            content={
              isCC2COrder
                ? t('Receipt.ReceiptDetails.CC2C.Text.Payee', uniqueCarrierNames)
                : t('Receipt.ReceiptDetails.Text.Payee')
            }
          />
        </div>
      </CartFooter>
    </Stack>
  );
};

ReceiptPriceSpecification.propTypes = {
  addOnOrders: PropTypes.arrayOf(AddOnOrder),
  bounds: boundsPropTypes,
  bundles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      optionId: PropTypes.string,
      price: PropTypes.number,
      products: PropTypes.arrayOf(PropTypes.string),
      title: PropTypes.string,
    }),
  ),
  cc2carrierBreakdown: PropTypes.arrayOf(
    PropTypes.shape({
      carrierName: PropTypes.string,
      transactionAmount: PropTypes.shape({
        price: PropTypes.shape({
          currency: PropTypes.shape({
            exponent: PropTypes.number,
            name: PropTypes.string,
          }),
          value: PropTypes.number,
        }),
      }),
    }),
  ),
  chargeCurrencyPriceFormat: PriceFormat,
  discountPrice: PropTypes.number,
  instalmentsTotalFee: PropTypes.number,
  isOtherChargeCurrency: PropTypes.bool,
  marketingCarrierNames: PropTypes.string,
  orderExtraProducts: PropTypes.arrayOf(PropTypes.object),
  paymentTypes: PropTypes.arrayOf(PropTypes.object),
  priceInChargeCurrency: PropTypes.number,
  totalPrice: PropTypes.number,
  totalPriceIncludingAddOnOrders: PropTypes.number,
  travelerPrices: PropTypes.arrayOf(
    PropTypes.shape({
      ageType: PropTypes.string,
      markup: PropTypes.number,
      price: PropTypes.number,
      quantity: PropTypes.number,
      totalPrice: PropTypes.number,
      totalPriceSum: PropTypes.number,
      vat: PropTypes.number,
    }),
  ),
};

export default ReceiptPriceSpecification;
